import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Avatar,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Tooltip,
  useTheme,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  Checkbox,
} from "@mui/material";
import {
  Search,
  Upgrade,
  PlayCircleOutline,
  ContentCopyOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";
import ListIcon from "@mui/icons-material/List";
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import HeaderTitle from "../../components/common/HeaderTitle/HeaderTitle";
import { API_ENDPOINT } from "../../services/api/resources";
import SearchBar from "../../components/common/SearchBar/SearchBar";

const UserAccess = () => {
  const theme = useTheme();
  const adminData = useSelector((state) => state.loginData.value);
  const admin_token = adminData?.token;
  const auth_token = adminData?.auth_token;

  // ---------- States ----------
  const [buyerlist, setBuyerList] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    total_pages: 1,
    total_records: 0,
    page_size: 10,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [licenseFilter, setLicenseFilter] = useState("All");
  const [orderBy, setOrderBy] = useState("first_name");
  const [order, setOrder] = useState("asc");
  const [loading, setLoading] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [open, setOpen] = useState(false);

  // Menu-related
  const [menulist, setMenuList] = useState({});
  const [selectedUserForMenu, setSelectedUserForMenu] = useState(null);
  const [menuDialogOpen, setMenuDialogOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});

  const [savingMap, setSavingMap] = useState({});

  const formatDate = (isoDate) => {
    if (!isoDate) return "NA";
    const date = new Date(isoDate);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // ---------- Helpers ----------
  const getRoleType = (user) => user?.role_type || "buyer";

  const getRoleMenus = (roleType) => {
    if (Array.isArray(menulist)) return menulist;
    if (typeof menulist === "object" && menulist !== null) {
      const arr = menulist[roleType];
      return Array.isArray(arr) ? arr : [];
    }
    return [];
  };

  const getMenuNames = (menuIds, roleType) => {
    const list = getRoleMenus(roleType);
    const labelMap = new Map(list.map((m) => [m.id, m.menu_lable]));
    return (menuIds || [])
      .map((id) => labelMap.get(id))
      .filter(Boolean)
      .join(", ");
  };

  const buildNestedMenuTree = useCallback((flatListInput) => {
    const flatList = Array.isArray(flatListInput) ? flatListInput : [];
    const menuMap = {};
    const rootMenus = [];

    flatList.forEach((m) => {
      if (!m || m.id == null) return;
      menuMap[m.id] = { ...m, children: [] };
    });

    flatList.forEach((m) => {
      if (!m || m.id == null) return;
      if (m.parent_id == null) rootMenus.push(menuMap[m.id]);
      else if (menuMap[m.parent_id]) menuMap[m.parent_id].children.push(menuMap[m.id]);
    });

    return rootMenus;
  }, []);

  const hasPendingChanges = (userId) => !!pendingChanges[userId];

  // ---------- Data fetching ----------
  const fetch_all_buyer_admin = async () => {
    setLoading(true);
    try {
      const payload = {
        admin_token,
        page: pagination.page,
        page_size: pagination.page_size,
        search: searchQuery,
        license_status: licenseFilter,
        sort_field: orderBy,
        sort_order: order,
      };

      const response = await axios.post(`${API_ENDPOINT.all_buyer_admin}`, payload, {
        headers: { Authorization: `Bearer ${auth_token}` },
      });

      const data = response.data.results || [];
      const pageData = response.data.pagination || {};

      setBuyerList(data);
      setPagination((prev) => ({ ...prev, ...pageData }));
      setPendingChanges({});
    } catch (err) {
      setErrorMessage("Failed to fetch user data. Please try again.");
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetch_all_menu = async () => {
    try {
      const menuResponse = await axios.get(`${API_ENDPOINT.fetch_all_menu}`);
      setMenuList(menuResponse.data);
    } catch (err) {
      setErrorMessage("Failed to fetch menu data. Please try again.");
      setErrorDialogOpen(true);
    }
  };

  useEffect(() => {
    fetch_all_buyer_admin();
    fetch_all_menu();
  }, [searchQuery, licenseFilter, orderBy, order, pagination.page, pagination.page_size]);

  // ---------- Menu Toggle ----------
  const handleMenuToggle = (userId, menuId) => {
    const allMenusFlat = [
      ...(menulist.buyer || []),
      ...(menulist.buyer_admin || []),
      ...(menulist.user || []),
    ];

    const user = buyerlist.find((u) => u.id === userId);
    const collectAllChildren = (parentId) => {
      const directChildren = allMenusFlat.filter((m) => m.parent_id === parentId);
      return directChildren.reduce(
        (acc, child) => [...acc, child.id, ...collectAllChildren(child.id)],
        []
      );
    };
    const allChildIds = collectAllChildren(menuId);
    const parentMenuObj = allMenusFlat.find((m) => m.id === menuId);
    const parentMenuId = parentMenuObj ? parentMenuObj.parent_id : undefined;
    const currentAllowed =
      pendingChanges[userId]?.allowed_menu || user.allowed_menu || [];
    const isChecked = currentAllowed.includes(menuId);
    let updatedAllowed = [...currentAllowed];

    if (isChecked) {
      updatedAllowed = updatedAllowed.filter(
        (id) => id !== menuId && !allChildIds.includes(id)
      );
      if (parentMenuId) {
        const siblingIds = allMenusFlat
          .filter((m) => m.parent_id === parentMenuId)
          .map((m) => m.id);
        const anySiblingSelected = siblingIds.some((id) =>
          updatedAllowed.includes(id)
        );
        if (!anySiblingSelected)
          updatedAllowed = updatedAllowed.filter((id) => id !== parentMenuId);
      }
    } else {
      updatedAllowed.push(menuId, ...allChildIds);
      if (parentMenuId && !updatedAllowed.includes(parentMenuId))
        updatedAllowed.push(parentMenuId);
    }

    updatedAllowed = Array.from(new Set(updatedAllowed));
    setPendingChanges((prev) => ({
      ...prev,
      [userId]: { ...prev[userId], allowed_menu: updatedAllowed },
    }));
  };

  // const handleLicenseActivate = async (buyer) => {
  //   const currentAllowed =
  //     pendingChanges[buyer.id]?.allowed_menu || buyer.allowed_menu || [];

  //   if (currentAllowed.length === 0) {
  //     setErrorMessage(
  //       <span style={{ fontSize: "18px", fontWeight: "bold", color: "red" }}>
  //         Please select at least one menu before activating the license.
  //       </span>
  //     );
  //     setErrorDialogOpen(true);
  //     return;
  //   }

  //   try {
  //     setSavingMap((prev) => ({ ...prev, [buyer.id]: true }));
  //     const payload = {
  //       token: buyer.token,
  //       allowed_menu: currentAllowed,
  //       vendorCount:
  //         pendingChanges[buyer.id]?.vendorCount !== undefined
  //           ? pendingChanges[buyer.id]?.vendorCount
  //           : buyer.vendorCount || null,
  //     };

  //     await axios.post(`${API_ENDPOINT.licenseActivate}`, payload, {
  //       headers: { Authorization: `Bearer ${auth_token}` },
  //     });

  //     setSuccessMessage(
  //       <span style={{ fontSize: "18px", fontWeight: "bold", color: "green" }}>
  //         {buyer.first_name}'s license has been{" "}
  //         {buyer.license_active ? "upgraded" : "activated"} successfully!
  //       </span>
  //     );
  //     setSuccessDialogOpen(true);

  //     // clear pending changes for this user
  //     setPendingChanges((prev) => {
  //       const out = { ...prev };
  //       delete out[buyer.id];
  //       return out;
  //     });

  //     fetch_all_buyer_admin();
  //   } catch (error) {
  //     setErrorMessage(
  //       <span style={{ fontSize: "18px", fontWeight: "bold", color: "red" }}>
  //         License activation failed. Please try again.
  //       </span>
  //     );
  //     setErrorDialogOpen(true);
  //   } finally {
  //     setSavingMap((prev) => ({ ...prev, [buyer.id]: false }));
  //   }
  // };


  const handleLicenseActivate = async (buyer) => {
    const currentAllowed =
      pendingChanges[buyer.id]?.allowed_menu || buyer.allowed_menu || [];

    if (currentAllowed.length === 0) {
      setErrorMessage(
        <span style={{ fontSize: "16px", color: "red" }}>
          Please select at least one menu before activating the license.
        </span>
      );
      setErrorDialogOpen(true);
      return;
    }

    try {
      setSavingMap((prev) => ({ ...prev, [buyer.id]: true }));
      const payload = {
        token: buyer.token,
        allowed_menu: currentAllowed,
        vendorCount:
          pendingChanges[buyer.id]?.vendorCount !== undefined
            ? pendingChanges[buyer.id]?.vendorCount
            : buyer.vendorCount || null,
      };

      await axios.post(`${API_ENDPOINT.licenseActivate}`, payload, {
        headers: { Authorization: `Bearer ${auth_token}` },
      });

      setSuccessMessage(
        <span style={{ fontSize: "16px", color: "green" }}>
          {buyer.first_name}'s license has been{" "}
          {buyer.license_active ? "upgraded" : "activated"} successfully!
        </span>
      );
      setSuccessDialogOpen(true);

      // Clear pending changes and refresh list
      setPendingChanges((prev) => {
        const out = { ...prev };
        delete out[buyer.id];
        return out;
      });

      fetch_all_buyer_admin();
    } catch (error) {
      setErrorMessage(
        <span style={{ fontSize: "16px", color: "red" }}>
          License activation failed. Please try again.
        </span>
      );
      setErrorDialogOpen(true);
    } finally {
      setSavingMap((prev) => ({ ...prev, [buyer.id]: false }));
    }
  };


  // ---------- Menu Dialog ----------
  const handleOpenMenuDialog = (buyer) => {
    setSelectedUserForMenu(buyer);
    setMenuDialogOpen(true);
  };
  const handleCloseMenuDialog = () => {
    setMenuDialogOpen(false);
    setSelectedUserForMenu(null);
  };

  // ---------- Table Sorting ----------
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination((prev) => ({
      ...prev,
      page_size: parseInt(event.target.value, 10),
      page: 1,
    }));
  };

  const handleCopy = () => setOpen(true);

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);

  };

  const handleVendorCountChange = (userId, value) => {
    setPendingChanges((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        vendorCount: value,
      },
    }));
  };


  const getVendorMenuId = () => {
    // Combine all menu arrays into one flat array
    const allMenus = [
      ...(menulist.buyer || []),
      ...(menulist.buyer_admin || []),
      ...(menulist.user || []),
    ];

    // Find the matching menu item
    const vendorMenu = allMenus.find(
      (menu) => menu && menu.menu_lable === "Vendor & Merchant Onboarding"
    );

    return vendorMenu && vendorMenu.id ? vendorMenu.id : null;
  };



  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <HeaderTitle titleText={"Customer Command Centre"} />

      <Grid item xs={12} md={6} py={2}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Search Bar */}
          <Box sx={{ flex: 1 }}>
            <SearchBar
              value={searchQuery}
              onChange={(query) => {
                setSearchQuery(query);
                setPagination(0);
              }}
            />
          </Box>


          <FormControl size="medium" sx={{ minWidth: 220 }}>
            <InputLabel sx={{ fontSize: "14px" }}>License Status</InputLabel>
            <Select
              value={licenseFilter}
              label="License Status"
              onChange={(e) => setLicenseFilter(e.target.value)}
              sx={{ fontSize: "14px" }}
            >
              <MenuItem value="All" sx={{ fontSize: "13px" }}>
                All Statuses
              </MenuItem>
              <MenuItem value="Active" sx={{ fontSize: "13px" }}>
                Active Only
              </MenuItem>
              <MenuItem value="Inactive" sx={{ fontSize: "13px" }}>
                Inactive Only
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Grid>

      {/* ---- Table ---- */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2, overflowX: "auto" }}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow sx={{
                backgroundColor: "rgb(224, 233, 238)",
                "& .MuiTableCell-root": {
                  "&:focus": {
                    backgroundColor: "rgb(224, 233, 238) !important",
                  },
                },
              }}>

                {[
                  { label: "Profile" },
                  {
                    label: "First Name",
                    sortable: true,
                    key: "first_name",
                  },
                  { label: "Last Name" },
                  { label: "Email" },
                  { label: "Company" },
                  {
                    label: "Created Date",
                    sortable: true,
                    key: "created_date"
                  },
                  { label: "Status" },
                  { label: "Access" },
                  { label: "Actions", align: "center" },
                ].map((header) => (
                  <TableCell
                    key={header.label}
                    align={header.align || "left"}
                    sx={{

                      fontSize: "14px",
                      fontWeight: 600,
                      textTransform: "capitalize",
                      border: "none",
                      fontFamily: "GilroySemiBold",
                      whiteSpace: "nowrap",
                      // overflow:"hidden",
                      textOverflow: "ellipsis",

                      py: 2,
                      "&:focus": {
                        backgroundColor: "rgb(224, 233, 238) !important",
                        outline: "none",
                      },
                    }}
                  >
                    {header.sortable ? (
                      <TableSortLabel
                        active={orderBy === header.key}
                        direction={orderBy === header.key ? order : "asc"}
                        onClick={() => handleSort(header.key)}
                        IconComponent={UnfoldMoreIcon}
                        sx={{
                          "& .MuiTableSortLabel-icon": {
                            opacity: 1,
                            color: "inherit !important",
                          },
                          "&.MuiTableSortLabel-root": {
                            color: "inherit",
                          },
                          "&.MuiTableSortLabel-root:hover": {
                            color: "inherit",
                            backgroundColor: "transparent",
                          },
                          "&.MuiTableSortLabel-root.Mui-focusVisible": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        {header.label}
                      </TableSortLabel>
                    ) : (
                      header.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody sx={{

              backgroundColor: "white",



              borderBottom: "1px solid #97979720",
            }}>
              {buyerlist.length > 0 ? (
                buyerlist.map((buyer) => (
                  <TableRow key={buyer.id} hover>
                    <TableCell sx={{
                      fontSize: "14px",
                      border: "none",
                      py: 2,
                      fontFamily: "GilroyMedium",
                      maxWidth: "150px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>
                      <Avatar
                        src={
                          buyer.profile_image
                            ? buyer.profile_image.startsWith("https://teckost.s3.amazonaws.com")
                              ? buyer.profile_image.replace(
                                "https://teckost.s3.amazonaws.com",
                                process.env.REACT_APP_CLOUDFRONT_URL
                              )
                              : buyer.profile_image
                            : "/no-avatar.png" // fallback image
                        }
                        alt={buyer.first_name || "User"}
                        sx={{ width: 40, height: 40 }}
                      />
                    </TableCell>
                    <TableCell sx={{
                      fontSize: "14px",
                      border: "none",
                      py: 2,
                      fontFamily: "GilroyMedium",
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>{buyer.first_name}</TableCell>
                    <TableCell sx={{
                      fontSize: "14px",
                      border: "none",
                      py: 2,
                      fontFamily: "GilroyMedium",
                      maxWidth: "150px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>{buyer.last_name}</TableCell>
                    <TableCell sx={{
                      fontSize: "14px",
                      border: "none",
                      py: 2,
                      fontFamily: "GilroyMedium",
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>{buyer.email}</TableCell>
                    <TableCell sx={{
                      fontSize: "14px",
                      border: "none",
                      py: 2,
                      fontFamily: "GilroyMedium",
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>{buyer.company_name}

                    </TableCell>


                    <TableCell sx={{
                      fontSize: "14px",
                      border: "none",
                      py: 2,
                      fontFamily: "GilroyMedium",
                      maxWidth: "100px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}>{formatDate(buyer.created_date)}

                    </TableCell>


                    <TableCell sx={{ border: "none" }} >
                      {buyer.license_active ? (
                        <Chip label="Active" color="success" size="small" sx={{ fontSize: "12px" }} />
                      ) : (
                        <Chip label="Inactive" color="warning" size="small" variant="outlined" sx={{ fontSize: "12px" }} />
                      )}
                    </TableCell>

                    <TableCell sx={{ border: "none", maxWidth: "150px" }}>
                      <Tooltip
                        title={
                          <Typography sx={{ fontSize: "12px", lineHeight: 1.4 }}>
                            {getMenuNames(
                              pendingChanges[buyer.id]?.allowed_menu || buyer.allowed_menu,
                              getRoleType(buyer)
                            )}
                          </Typography>
                        }
                      >
                        <Chip
                          icon={<ListIcon sx={{ fontSize: "14px" }} />}
                          label={`${(
                            pendingChanges[buyer.id]?.allowed_menu || buyer.allowed_menu || []
                          ).length} Menus`}
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenMenuDialog(buyer)}
                          color={hasPendingChanges(buyer.id) ? "primary" : "default"}
                          sx={{
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: 500,
                            px: 0.5,
                            py: 0.5,
                            "& .MuiChip-label": {
                              fontSize: "12px",
                            },
                          }}
                        />
                      </Tooltip>

                    </TableCell>

                    <TableCell align="center" sx={{ border: "none" }}>
                      <Tooltip
                        title={
                          <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
                            {buyer.license_active ? "Upgrade License" : "Activate License"}
                          </Typography>
                        }

                      >
                        <Button
                          variant="contained"
                          color={buyer.license_active ? "secondary" : "primary"}
                          size="small"
                          startIcon={buyer.license_active ? <Upgrade /> : <PlayCircleOutline />}
                          onClick={() => handleLicenseActivate(buyer)}
                          disabled={savingMap[buyer.id] || (pendingChanges[buyer.id]?.allowed_menu || buyer.allowed_menu || []).length === 0}
                          sx={{ fontSize: "13px", textTransform: "none" }}
                        >
                          {savingMap[buyer.id]
                            ? "Processing..."
                            : buyer.license_active
                              ? "Upgrade"
                              : "Activate"}
                        </Button>

                      </Tooltip>

                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography>No users found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 0.5, fontSize: "14px", px: 1 }}
            >
              {pagination.total_records > 0
                ? `Showing ${(pagination.page - 1) * pagination.page_size + 1}–${Math.min(
                  pagination.page * pagination.page_size,
                  pagination.total_records
                )} of ${pagination.total_records} users`
                : "No users to display"}
            </Typography>


            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={pagination.total_records}
              rowsPerPage={pagination.page_size}
              page={pagination.page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                "& .MuiTablePagination-toolbar": { fontSize: "14px", minHeight: "60px" },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "12px" },
              }}
            />
          </Box>
        </TableContainer>
      )}

      {/* ---- Menu Dialog ---- */}

      <Dialog
        open={menuDialogOpen}
        onClose={handleCloseMenuDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          sx={{
            fontSize: "17px",
            fontWeight: "bold",
            color: "primary.main",
          }}
        >
          <span style={{ fontWeight: "600" }}>
            {selectedUserForMenu?.first_name} {selectedUserForMenu?.last_name}
          </span>{" "}
          – Menu Access
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ fontSize: "15px" }}>
          {selectedUserForMenu &&
            ["buyer", "buyer_admin", "user"].map((roleKey) => {
              const roleMenus = menulist[roleKey] || [];
              if (roleMenus.length === 0) return null;

              const nestedMenus = buildNestedMenuTree(roleMenus);

              return (
                <Box key={roleKey} sx={{ mb: 3 }}>
                  {/* ---- Section Header ---- */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "14px",
                      color: "primary.main",
                      mb: 1,
                      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                      textTransform: "capitalize",
                    }}
                  >
                    {roleKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </Typography>

                  {/* ---- Menu Items ---- */}
                  {nestedMenus.map((menu) => (
                    <Box key={menu.id} sx={{ mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          checked={(
                            pendingChanges[selectedUserForMenu.id]?.allowed_menu ||
                            selectedUserForMenu.allowed_menu
                          )?.includes(menu.id)}
                          onChange={() => handleMenuToggle(selectedUserForMenu.id, menu.id)}
                          size="medium"
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "600", fontSize: "13px" }}
                        >
                          {menu.menu_lable}
                        </Typography>
                      </Box>

                      {menu.children?.map((child) => (
                        <Box
                          key={child.id}
                          sx={{
                            pl: 5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Checkbox
                            checked={(
                              pendingChanges[selectedUserForMenu.id]?.allowed_menu ||
                              selectedUserForMenu.allowed_menu
                            )?.includes(child.id)}
                            onChange={() =>
                              handleMenuToggle(selectedUserForMenu.id, child.id)
                            }
                            size="small"
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "13px" }}
                          >
                            {child.menu_lable}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}

                  {/* ---- Vendor Count ---- */}
                  {roleKey === "buyer" && (() => {
                    const vendorMenuId = getVendorMenuId();
                    const allowedMenus =
                      pendingChanges[selectedUserForMenu?.id]?.allowed_menu ||
                      selectedUserForMenu?.allowed_menu ||
                      [];

                    if (vendorMenuId && allowedMenus.includes(vendorMenuId)) {
                      return (
                        <Box sx={{ mt: 3 }}>
                          <Typography
                            variant="subtitle1"
                            gutterBottom
                            sx={{
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: "text.primary",
                            }}
                          >
                            Supplier Count
                          </Typography>
                          {/* <TextField
                            fullWidth
                            type="number"
                            size="small"
                            label="Maximum Vendor Count"
                            required
                            value={
                              pendingChanges[selectedUserForMenu.id]?.vendorCount !== undefined
                                ? pendingChanges[selectedUserForMenu.id]?.vendor_count
                                : selectedUserForMenu.vendor_count || ""
                            }
                            onChange={(e) =>
                              handleVendorCountChange(selectedUserForMenu.id, e.target.value === "" ? null : Number(e.target.value))
                            }
                            sx={{
                              mt: 1,
                              fontSize: "14px",
                            }}
                            InputLabelProps={{
                              sx: { fontSize: "14px" },
                            }}
                            InputProps={{
                              sx: { fontSize: "15px" },
                            }}
                          /> */}

                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            label="Maximum Supplier Count"
                            required
                            value={
                              pendingChanges[selectedUserForMenu.id]?.vendorCount ??
                              selectedUserForMenu.vendor_count ??
                              ""
                            }
                            onChange={(e) =>
                              handleVendorCountChange(
                                selectedUserForMenu.id,
                                e.target.value === "" ? "" : Number(e.target.value)
                              )
                            }
                            sx={{ mt: 1, fontSize: "14px" }}
                            InputLabelProps={{ sx: { fontSize: "14px" } }}
                            InputProps={{ sx: { fontSize: "15px" } }}
                          />




                        </Box>
                      );
                    }
                    return null;
                  })()}
                </Box>
              );
            })}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseMenuDialog}
            variant="outlined"
            sx={{
              fontSize: "13px",
              fontWeight: "bold",
              px: 2,
              py: 1,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>



      {/* ---- Snackbar ---- */}
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity="success" sx={{ width: "100%" }}>
          Copied!
        </Alert>
      </Snackbar>

      {/* ---- Success Dialog ---- */}
      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box display="flex" alignItems="center" flexDirection="column" textAlign="center" sx={{ p: 3 }}>
          <CheckCircleOutline color="success" sx={{ fontSize: 60, mb: 2 }} />
          <DialogTitle>Success</DialogTitle>
          <DialogContent>
            <Typography>{successMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => setSuccessDialogOpen(false)}>
              OK
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* ---- Error Dialog ---- */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} maxWidth="xs" fullWidth>
        <Box display="flex" alignItems="center" flexDirection="column" textAlign="center" sx={{ p: 3 }}>
          <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
            <DialogTitle>Error</DialogTitle>
            <DialogContent>
              <Typography>{errorMessage}</Typography>
            </DialogContent>
          </Alert>
          <DialogActions>
            <Button variant="contained" onClick={() => setErrorDialogOpen(false)}>
              OK
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default UserAccess;
